import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { ToolkitStore } from '@reduxjs/toolkit/dist/configureStore'
import { IRefreshResponse } from 'models/auth'
import { TOKEN } from 'shared/constants/app'
import { API_URL, INTEGRATION_API_URL } from 'shared/constants/urls'
import { appActions } from 'store/app'
import { isSameTokenUser } from 'shared/utils/auth/isSameTokenUser'
import { showMessage } from 'shared/utils/showMessage'

let store: ToolkitStore

export const injectStore = (_store: ToolkitStore) => {
  store = _store
}

// По этому заголовку бэкенд выдаёт панели отдельную куку сессии: иначе вход
// на сайте под другим аккаунтом подменяет пользователя в панели
const clientAppHeaders = () => ({ 'X-Client-App': 'admin' })

export const temporaryApi = axios.create({
  withCredentials: true,
  baseURL: API_URL,
  headers: clientAppHeaders()
})

export const api = axios.create({
  withCredentials: true,
  baseURL: API_URL,
  headers: clientAppHeaders()
})

export const privateApi = axios.create({
  withCredentials: true,
  baseURL: API_URL,
  headers: clientAppHeaders()
})

export const integrationApi = axios.create({
  baseURL: INTEGRATION_API_URL
})

const requestInterceptor = async (config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem(TOKEN)
  config.headers.Authorization = token ? `Bearer ${token}` : ''
  return config
}

temporaryApi.interceptors.response.use(
  (config) => {
    return config
  },
  async (error: any) => {
    const originalRequest = error.config
    if (
      error.response?.status === 401 &&
      error.config &&
      !error.config._isRetry
    ) {
      originalRequest._isRetry = true
      try {
        const response = await api.get<IRefreshResponse>('refresh')
        originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`
        return temporaryApi.request(originalRequest)
      } catch {
        showMessage(
          'error',
          'Ошибка',
          'Время для заполнения данных закончилось, обновите страничку'
        )
      }
    }
    throw error
  }
)

privateApi.interceptors.request.use(requestInterceptor)
integrationApi.interceptors.request.use(requestInterceptor)

let isRefreshing = false
let failedQueue: any[] = []

const processQueue = (error: any, token: any = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

const endSession = () => {
  showMessage(
    'error',
    'Сессия завершена',
    'Войдите в аккаунт заново'
  )
  store.dispatch(appActions.logout())
}

let refreshRequest: Promise<string> | null = null

const isSessionRejected = (error: any) =>
  [400, 401, 403].includes(error?.response?.status)

// Один /refresh на всех, кому нужен новый токен: запросам с 401 и чат-сокетам.
// Из аккаунта выходим, только если сервер отказал в сессии, а не при сбое сети
export const refreshAccessToken = (): Promise<string> => {
  if (refreshRequest) return refreshRequest

  refreshRequest = api
    .get<IRefreshResponse>('refresh')
    .then(
      ({ data }) => {
        if (!isSameTokenUser(data.access_token)) {
          endSession()
          throw new Error('Сессия принадлежит другому аккаунту')
        }
        store.dispatch(appActions.setToken(data))
        return data.access_token
      },
      (error) => {
        if (isSessionRejected(error)) endSession()
        throw error
      }
    )
    .finally(() => {
      refreshRequest = null
    })

  return refreshRequest
}

const FORBIDDEN_MESSAGE_INTERVAL = 10000
let lastForbiddenMessageAt = 0

// фоновые запросы могут получать 403 пачками — уведомление не чаще раза в 10 секунд
const showForbiddenMessage = () => {
  if (Date.now() - lastForbiddenMessageAt < FORBIDDEN_MESSAGE_INTERVAL) return
  lastForbiddenMessageAt = Date.now()
  showMessage(
    'error',
    'Недостаточно прав',
    'Действие недоступно для вашего аккаунта'
  )
}

const interceptorResponse = (apiInstance: AxiosInstance) => (error: any) => {
  // 403 — не хватает прав на конкретное действие, а не конец сессии:
  // раньше любой такой ответ молча выкидывал пользователя из аккаунта
  if (error.response?.status === 403) {
    showForbiddenMessage()
    throw error
  }

  const originalRequest = error.config

  if (
    error.response?.status === 401 &&
    error.config &&
    !error.config._isRetry
  ) {
    if (isRefreshing) {
      return new Promise(function (resolve, reject) {
        failedQueue.push({ resolve, reject })
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return apiInstance.request(originalRequest)
        })
        .catch((err) => {
          return err
        })
    }

    originalRequest._isRetry = true
    isRefreshing = true

    return new Promise(function (resolve, reject) {
      refreshAccessToken()
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          processQueue(null, token)
          resolve(axios(originalRequest))
        })
        .catch((err) => {
          processQueue(err, null)
          reject(err)
        })
        .then(() => {
          isRefreshing = false
        })
    })
  }

  throw error
}

privateApi.interceptors.response.use(
  (config) => {
    return config
  },
  interceptorResponse(privateApi)
)

integrationApi.interceptors.response.use(
  (config) => {
    return config
  },
  interceptorResponse(integrationApi)
)
